import { useEffect, useState } from 'react';

/**
 * Tiny module-level pub/sub for "the home tab has finished its first
 * data fetch." Lets the root layout keep a single Preloader instance
 * mounted across the entire boot — font load + DB warmup + home data —
 * and only fade it out once the user-visible app has real content to
 * show.
 *
 * Without this signal, the previous architecture used two separate
 * Preloader mounts (one in _layout for the font phase, one in the home
 * tab for the DB-warmup phase). Each mount restarted the breathing /
 * ring / tagline animations from frame zero, which read to the user
 * as visible "jitter" between the two phases.
 *
 * The signal is one-shot and process-scoped: once set true it stays
 * true. The home tab is the canonical caller (it owns the slowest
 * portion of the boot path) but any screen can call markBootReady()
 * when it has paint-worthy content.
 */

let ready = false;
const listeners = new Set<() => void>();

export function markBootReady(): void {
  if (ready) return;
  ready = true;
  for (const fn of listeners) fn();
}

export function isBootReady(): boolean {
  return ready;
}

/**
 * React hook — subscribes to the boot-ready signal and re-renders the
 * caller when it flips. Safe to call from any tree depth; the underlying
 * listener Set is global.
 */
export function useBootReady(): boolean {
  const [v, setV] = useState(ready);
  useEffect(() => {
    if (ready) {
      // Already true at subscription time — make sure local state agrees.
      setV(true);
      return;
    }
    const fn = () => setV(true);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return v;
}
